import { genericActions } from "../../Everou/_actions/generic.actions";
import { ORGANIZATIONS_ACTION_TYPES } from "../_actionTypes/ORGANIZATIONS_ACTION_TYPES";
import { OrganizationMapper } from "../_mappers/OrganizationMapper";
import { OrganizationMemberRole } from "../_models/Organization";
import { OrganizationsService } from "../_services/OrganizationsService";

export const OrganizationsActions = {
    listOrganizations,
    selectOrganization,
    getOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    addUserToOrganization,
    removeUserFromOrganization,
    changeMemberRole,
    addLocationToOrganization,
    addAllLocationsToOrganization,
    removeLocationFromOrganization,
    getAvailableLocations,
    clearAvailableLocations,
};

const AVAILABLE_LOCATIONS_PAGE_SIZE = 50;

function listRequest() {
    return { type: ORGANIZATIONS_ACTION_TYPES.LIST_REQUEST };
}

function listSuccess(items) {
    return { type: ORGANIZATIONS_ACTION_TYPES.LIST_SUCCESS, items };
}

function listFailure() {
    return { type: ORGANIZATIONS_ACTION_TYPES.LIST_FAILURE };
}

function detailRequest() {
    return { type: ORGANIZATIONS_ACTION_TYPES.DETAIL_REQUEST };
}

function detailSuccess(detail) {
    return { type: ORGANIZATIONS_ACTION_TYPES.DETAIL_SUCCESS, detail };
}

function detailFailure() {
    return { type: ORGANIZATIONS_ACTION_TYPES.DETAIL_FAILURE };
}

function listOrganizations() {
    return async (dispatch) => {
        await dispatch(
            genericActions.genericAsyncAction(asyncAction, () => {
                dispatch(listFailure());
            }),
        );

        async function asyncAction() {
            dispatch(listRequest());
            const response = await OrganizationsService.list();
            const items = OrganizationMapper.mapAllServerToLocal(
                response.organizations ?? response,
            );
            dispatch(listSuccess(items));
        }
    };
}

function selectOrganization(uid: string | null) {
    return { type: ORGANIZATIONS_ACTION_TYPES.SELECT, uid };
}

function getOrganization(uid: string) {
    return async (dispatch) => {
        await dispatch(
            genericActions.genericAsyncAction(asyncAction, () => {
                dispatch(detailFailure());
            }),
        );

        async function asyncAction() {
            dispatch(detailRequest());
            const response = await OrganizationsService.get(uid);
            const detail = OrganizationMapper.mapDetailServerToLocal(response);
            dispatch(detailSuccess(detail));
        }
    };
}

function createOrganization(name: string) {
    return async (dispatch) => {
        return await dispatch(
            genericActions.genericAsyncAction(asyncAction, null, true),
        );

        async function asyncAction() {
            const response = await OrganizationsService.create({ name });
            await dispatch(listOrganizations());
            return OrganizationMapper.mapServerToLocal(response);
        }
    };
}

function updateOrganization(uid: string, name: string) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.update({ uid, name });
            await dispatch(listOrganizations());
            await dispatch(getOrganization(uid));
        }
    };
}

function deleteOrganization(uid: string) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.remove(uid);
            dispatch(selectOrganization(null));
            await dispatch(listOrganizations());
        }
    };
}

function addUserToOrganization(
    uid: string,
    email: string,
    role: OrganizationMemberRole,
) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.addUser({ uid, email, role });
            await dispatch(getOrganization(uid));
        }
    };
}

function removeUserFromOrganization(uid: string, userUid: string) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.removeUser({ uid, userUid });
            await dispatch(getOrganization(uid));
        }
    };
}

function changeMemberRole(
    uid: string,
    userUid: string,
    role: OrganizationMemberRole,
) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.changeUserRole({ uid, userUid, role });
            await dispatch(getOrganization(uid));
        }
    };
}

function addLocationToOrganization(
    uid: string,
    locationUid: string,
    searchTerm?: string,
) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.addLocation({ uid, locationUid });
            await dispatch(getOrganization(uid));
            await dispatch(getAvailableLocations(uid, { searchTerm }));
        }
    };
}

function addAllLocationsToOrganization(uid: string, searchTerm?: string) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.addAllLocations(uid, searchTerm);
            await dispatch(getOrganization(uid));
            await dispatch(getAvailableLocations(uid, { searchTerm }));
        }
    };
}

function removeLocationFromOrganization(uid: string, locationUid: string) {
    return async (dispatch) => {
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            await OrganizationsService.removeLocation({ uid, locationUid });
            await dispatch(getOrganization(uid));
            await dispatch(getAvailableLocations(uid));
        }
    };
}

// Monotonic tag for available-locations requests: the reducer keeps the id
// of the latest request per organization and discards any response carrying
// an older one, so interleaved responses (fast typing, a request still in
// flight when the modal reopens) can never overwrite the newest page. Same
// discard-stale-by-tag pattern as the customization template store, which
// tags its slot with the organization uid.
let availableLocationsRequestId = 0;

function getAvailableLocations(
    uid: string,
    { searchTerm, offset = 0 }: { searchTerm?: string; offset?: number } = {},
) {
    return async (dispatch) => {
        const requestId = ++availableLocationsRequestId;
        dispatch({
            type: ORGANIZATIONS_ACTION_TYPES.AVAILABLE_LOCATIONS_REQUEST,
            uid,
            requestId,
        });
        await dispatch(genericActions.genericAsyncAction(asyncAction));

        async function asyncAction() {
            const response = await OrganizationsService.availableLocations(
                uid,
                {
                    searchTerm,
                    offset,
                    limit: AVAILABLE_LOCATIONS_PAGE_SIZE,
                },
            );
            const locations = (response.locations ?? response ?? []).map(
                OrganizationMapper.mapLocationRefServerToLocal,
            );
            dispatch({
                type: ORGANIZATIONS_ACTION_TYPES.AVAILABLE_LOCATIONS_SUCCESS,
                uid,
                locations,
                total: response.total ?? locations.length,
                // The reducer only appends when the stored page belongs to
                // the same search term, so pages of different filters can't
                // get mixed by interleaved responses.
                searchTerm: searchTerm ?? null,
                append: offset > 0,
                // Lets the reducer refuse duplicate appends: two rapid
                // load-more clicks request the same offset, only the first
                // extends the page.
                offset,
                requestId,
            });
        }
    };
}

// Empties the stored page (e.g. when the add-location modal opens) so stale
// results from a previous filter are never shown while the fresh ones load.
// Bumps the request tag too, so a response still in flight from before the
// clear cannot land on top of the emptied page.
function clearAvailableLocations(uid: string) {
    return (dispatch) => {
        const requestId = ++availableLocationsRequestId;
        dispatch({
            type: ORGANIZATIONS_ACTION_TYPES.AVAILABLE_LOCATIONS_REQUEST,
            uid,
            requestId,
        });
        dispatch({
            type: ORGANIZATIONS_ACTION_TYPES.AVAILABLE_LOCATIONS_SUCCESS,
            uid,
            locations: [],
            total: 0,
            searchTerm: null,
            append: false,
            offset: 0,
            requestId,
        });
    };
}
